import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Lock, Tag, X } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { QtyControl } from "../components/CartDrawer";
import { RelayPicker } from "../components/RelayPicker";
import { api, formatError, formatPrice, imgUrl } from "../lib/api";
import { useSeo } from "../lib/hooks";

const Field = ({ label, testid, className = "", ...props }) => (
  <label className={`block ${className}`}>
    <span className="text-[10px] tracking-[0.2em] uppercase text-[#F8F6F1]/50">{label}</span>
    <input {...props} data-testid={testid} className="mt-1.5 w-full bg-transparent border border-[#C9A45C]/25 px-4 py-3 text-sm outline-none focus:border-[#C9A45C] transition-colors" />
  </label>
);

const Step = ({ n, title, children }) => (
  <section className="border border-[#C9A45C]/15 p-5 sm:p-7">
    <p className="font-display text-2xl mb-5"><span className="text-[#C9A45C] mr-3">{n}.</span>{title}</p>
    {children}
  </section>
);

export default function Checkout() {
  useSeo({ title: "Finaliser ma commande" });
  const { items, update, remove } = useCart();
  const nav = useNavigate();
  const continueShopping = () => (window.history.state?.idx > 0 ? nav(-1) : nav("/boutique"));
  const [methods, setMethods] = useState([]);
  const [method, setMethod] = useState("");
  const [customer, setCustomer] = useState({ first_name: "", last_name: "", email: "", phone: "" });
  const [address, setAddress] = useState({ line1: "", line2: "", postal_code: "", city: "", country: "France" });
  const [relay, setRelay] = useState(null);
  const [config, setConfig] = useState({});
  const [notes, setNotes] = useState("");
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [reminderConsent, setReminderConsent] = useState(false);
  const [consentedEmail, setConsentedEmail] = useState("");

  useEffect(() => {
    api.get("/shipping-methods").then((r) => { setMethods(r.data); if (r.data[0]) setMethod(r.data[0].id); });
    api.get("/config").then((r) => setConfig(r.data));
    api.get("/account/profile").then(({ data }) => {
      setCustomer((c) => ({ ...c, first_name: data.first_name || "", last_name: data.last_name || "", email: data.email, phone: data.phone || "" }));
      if (data.address?.line1) setAddress((a) => ({ ...a, ...data.address }));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!items.length) { setQuote(null); return; }
    api.post("/cart/quote", { items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })), shipping_method: method || null, promo_code: promo })
      .then((r) => {
        setQuote(r.data); setQuoteError(null);
        if (r.data.promo_error && promo) { toast.error(r.data.promo_error); setPromo(null); }
      })
      .catch((e) => setQuoteError(formatError(e)));
  }, [items, method, promo]);

  useEffect(() => {
    const email = customer.email.trim().toLowerCase();
    if (!reminderConsent || !items.length || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    const timer = setTimeout(() => {
      api.post("/cart/reminder-consent", {
        email, consent: reminderConsent,
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
      }).then(() => setConsentedEmail(email)).catch(() => {});
    }, 1200);
    return () => clearTimeout(timer);
  }, [customer.email, reminderConsent, items]);

  const changeReminderConsent = (checked) => {
    setReminderConsent(checked);
    if (!checked && consentedEmail) {
      api.post("/cart/reminder-consent", { email: consentedEmail, consent: false, items: [] })
        .then(() => setConsentedEmail(""))
        .catch(() => toast.error("Impossible de retirer votre accord. Utilisez le lien de désinscription reçu par e-mail."));
    }
  };

  const options = quote?.shipping_options || methods;
  const current = options.find((m) => m.id === method);
  const setC = (k) => (e) => setCustomer({ ...customer, [k]: e.target.value });
  const setA = (k) => (e) => setAddress({ ...address, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (method === "point_relais" && !relay) { toast.error("Veuillez sélectionner un point relais sur la carte"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        customer: { ...customer, phone: customer.phone || null },
        shipping_method: method,
        address: current?.requires_address ? { ...address, line2: address.line2 || null } : null,
        relay_point: method === "point_relais" ? relay : null,
        promo_code: promo, notes: notes || null, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatError(err));
      setSubmitting(false);
    }
  };

  if (!items.length)
    return (
      <div className="max-w-xl mx-auto px-5 py-32 text-center" data-testid="checkout-empty">
        <h1 className="font-display text-4xl">Votre panier est vide</h1>
        <Link to="/boutique" className="btn-gold mt-8">Découvrir nos parfums</Link>
      </div>
    );

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12" data-testid="checkout-page">
      <button type="button" onClick={continueShopping} className="mb-6 inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#C9A45C] hover:text-[#E5C178] transition-colors" data-testid="checkout-continue-shopping-button">
        <ArrowLeft className="h-4 w-4" /> Continuer mes achats
      </button>
      <h1 className="font-display text-4xl sm:text-5xl">Finaliser ma commande</h1>
      {config.test_mode && <div className="mt-5 border border-[#C9A45C]/40 bg-[#C9A45C]/5 px-4 py-3 text-xs sm:text-sm text-[#E5C178]" data-testid="checkout-test-mode-notice">
        Mode test — aucun paiement réel ne sera effectué. Carte de test : 4242 4242 4242 4242, date future, CVC au choix.
      </div>}
      <form onSubmit={submit} className="mt-10 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7 space-y-6 min-w-0">
          <Step n={1} title="Vos coordonnées">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Prénom" required value={customer.first_name} onChange={setC("first_name")} autoComplete="given-name" testid="checkout-first-name" />
              <Field label="Nom" required value={customer.last_name} onChange={setC("last_name")} autoComplete="family-name" testid="checkout-last-name" />
              <Field label="E-mail" type="email" required value={customer.email} onChange={setC("email")} autoComplete="email" testid="checkout-email" />
              <Field label={current?.rate_key ? "Téléphone pour la livraison" : "Téléphone (facultatif)"} type="tel" required={Boolean(current?.rate_key)} value={customer.phone} onChange={setC("phone")} autoComplete="tel" testid="checkout-phone" />
            </div>
            <label className="mt-5 flex items-start gap-3 text-xs text-[#F8F6F1]/70">
              <input type="checkbox" checked={reminderConsent} onChange={(e) => changeReminderConsent(e.target.checked)} className="mt-0.5 accent-[#C9A45C]" data-testid="checkout-reminder-consent" />
              <span>J'accepte de recevoir un e-mail de rappel si je ne termine pas ma commande. Facultatif, sans incidence sur mon achat. Je pourrai me désinscrire à tout moment.</span>
            </label>
          </Step>
          <Step n={2} title="Mode de livraison">
            {quote?.free_remaining > 0 && (
              <p className="mb-4 text-sm text-[#E5C178] border border-[#C9A45C]/30 bg-[#C9A45C]/5 p-3" data-testid="free-shipping-remaining">Plus que {formatPrice(quote.free_remaining)} pour bénéficier de la livraison offerte.</p>
            )}
            {quote && quote.free_remaining === 0 && <p className="mb-4 text-sm text-emerald-400" data-testid="free-shipping-reached">Livraison offerte sur les expéditions !</p>}
            <div className="space-y-3" role="radiogroup">
              {options.map((m) => (
                <label key={m.id} className={`flex items-start gap-4 p-4 border transition-colors duration-300 ${!m.available ? "opacity-45 cursor-not-allowed border-[#C9A45C]/10" : method === m.id ? "border-[#C9A45C] bg-[#C9A45C]/5 cursor-pointer" : "border-[#C9A45C]/20 hover:border-[#C9A45C]/50 cursor-pointer"}`} data-testid={`shipping-option-${m.id}`}>
                  <input type="radio" name="shipping" disabled={!m.available} checked={method === m.id} onChange={() => setMethod(m.id)} className="mt-1 accent-[#C9A45C]" />
                  <span className="flex-1">
                    <span className="block text-sm font-medium">{m.label}</span>
                    <span className="block text-xs text-[#F8F6F1]/50 mt-0.5">{m.description}{m.carrier ? ` · ${m.carrier}` : ""}</span>
                    {m.reason && <span className="block text-xs text-red-300 mt-0.5">{m.reason}</span>}
                  </span>
                  <span className="text-sm text-[#C9A45C] whitespace-nowrap" data-testid={`shipping-price-${m.id}`}>{m.available === false ? "—" : m.price > 0 ? formatPrice(m.price) : "Gratuit"}</span>
                </label>
              ))}
            </div>
            {quote && <p className="mt-3 text-xs text-[#F8F6F1]/40" data-testid="parcel-weight">Poids estimé du colis emballé : {quote.weight_g} g</p>}
            {current?.requires_address && (
              <div className="grid sm:grid-cols-2 gap-4 mt-6" data-testid="checkout-address-form">
                <Field className="sm:col-span-2" label="Adresse" required value={address.line1} onChange={setA("line1")} autoComplete="address-line1" testid="checkout-address-line1" />
                <Field className="sm:col-span-2" label="Complément (facultatif)" value={address.line2} onChange={setA("line2")} autoComplete="address-line2" testid="checkout-address-line2" />
                <Field label="Code postal" required value={address.postal_code} onChange={setA("postal_code")} autoComplete="postal-code" testid="checkout-postal-code" />
                <Field label="Ville" required value={address.city} onChange={setA("city")} autoComplete="address-level2" testid="checkout-city" />
              </div>
            )}
            {current?.rate_key && !current.carrier_confirmed && config.test_mode && (
              <p className="mt-4 text-xs text-[#E5C178] border border-dashed border-[#C9A45C]/40 p-3" data-testid="shipping-unconfirmed-notice">Mode test : expédition en attente de confirmation d'un transporteur.</p>
            )}
            {method === "point_relais" && (
              <RelayPicker apiKey={config.sendcloud_service_point_key} postalCode={address.postal_code} carrier={current?.carrier} value={relay} onChange={setRelay} />
            )}
            {method === "retrait" && <p className="mt-5 text-xs text-[#F8F6F1]/55">Le lieu et l'horaire du retrait vous seront communiqués après la commande.</p>}
          </Step>
          <Step n={3} title="Remarque (facultatif)">
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500} rows={3} className="w-full bg-transparent border border-[#C9A45C]/25 px-4 py-3 text-sm outline-none focus:border-[#C9A45C]" data-testid="checkout-notes" />
          </Step>
        </div>

        <aside className="lg:col-span-5 min-w-0">
          <div className="lg:sticky lg:top-32 border border-[#C9A45C]/25 bg-[#101010] p-5 sm:p-7" data-testid="checkout-summary">
            <p className="font-display text-2xl mb-5">Récapitulatif</p>
            <ul className="divide-y divide-[#C9A45C]/10">
              {items.map((i) => (
                <li key={i.product_id} className="py-4 flex gap-3" data-testid={`checkout-item-${i.slug}`}>
                  <img src={imgUrl(i.image)} alt={i.name} className="h-16 w-14 object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{i.name}</p>
                    {i.volume_ml > 0 && <p className="text-xs text-[#F8F6F1]/45">{i.volume_ml} ml</p>}
                    <div className="mt-2 flex items-center gap-2">
                      <QtyControl value={i.quantity} max={Math.min(i.stock || 20, 20)} onChange={(q) => update(i.product_id, q)} testid={`checkout-qty-${i.slug}`} />
                      <button type="button" onClick={() => remove(i.product_id)} className="text-xs text-[#F8F6F1]/45 hover:text-red-400" data-testid={`checkout-remove-${i.slug}`}>Retirer</button>
                    </div>
                  </div>
                  <p className="text-sm text-[#C9A45C]">{formatPrice(i.price * i.quantity)}</p>
                </li>
              ))}
            </ul>
            <div className="mt-5">
              {promo ? (
                <div className="flex items-center justify-between border border-[#C9A45C]/40 px-4 py-3 text-sm" data-testid="promo-applied">
                  <span className="flex items-center gap-2"><Tag className="h-4 w-4 text-[#C9A45C]" />{promo}</span>
                  <button type="button" onClick={() => setPromo(null)} aria-label="Retirer le code" data-testid="promo-remove"><X className="h-4 w-4" /></button>
                </div>
              ) : (
                <div className="flex">
                  <input value={promoInput} onChange={(e) => setPromoInput(e.target.value.toUpperCase())} placeholder="Code promo" className="flex-1 bg-transparent border border-[#C9A45C]/25 px-4 py-3 text-sm outline-none focus:border-[#C9A45C]" data-testid="promo-input" />
                  <button type="button" onClick={() => promoInput.trim() && setPromo(promoInput.trim())} className="px-5 border border-l-0 border-[#C9A45C]/25 text-xs tracking-[0.2em] uppercase hover:bg-[#C9A45C] hover:text-[#0B0B0B] transition-colors" data-testid="promo-apply">Appliquer</button>
                </div>
              )}
            </div>
            {quoteError && <p className="mt-4 text-sm text-red-400" data-testid="checkout-quote-error">{quoteError}</p>}
            {quote && (
              <dl className="mt-6 space-y-2 text-sm">
                <div className="flex justify-between"><dt className="text-[#F8F6F1]/60">Sous-total</dt><dd data-testid="summary-subtotal">{formatPrice(quote.subtotal)}</dd></div>
                {quote.discount > 0 && <div className="flex justify-between text-[#C9A45C]"><dt>Réduction ({quote.promo_code})</dt><dd data-testid="summary-discount">−{formatPrice(quote.discount)}</dd></div>}
                <div className="flex justify-between"><dt className="text-[#F8F6F1]/60">Livraison</dt><dd data-testid="summary-shipping">{quote.shipping_cost > 0 ? formatPrice(quote.shipping_cost) : "Gratuit"}</dd></div>
                <div className="gold-line !my-4" />
                <div className="flex justify-between items-baseline"><dt className="eyebrow">Total TTC</dt><dd className="font-display text-3xl text-[#C9A45C]" data-testid="summary-total">{formatPrice(quote.total)}</dd></div>
              </dl>
            )}
            <button type="submit" disabled={submitting || !!quoteError || !method} className="btn-gold w-full mt-6" data-testid="checkout-stripe-button">
              <Lock className="h-4 w-4" /> {submitting ? "Redirection…" : "Payer en toute sécurité"}
            </button>
            <p className="mt-3 text-[11px] text-center text-[#F8F6F1]/40">Paiement par carte, Apple Pay ou Google Pay selon disponibilité, via Stripe. Aucune donnée bancaire n'est stockée par NasDeal.</p>
            <p className="mt-2 text-[11px] text-center text-[#F8F6F1]/40">En validant, vous acceptez nos <Link to="/cgv" className="underline">conditions générales de vente</Link>.</p>
          </div>
        </aside>
      </form>
    </div>
  );
}

